// src/pages/Orders.js
import React, { useEffect, useState } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { SPECTRUM_PRODUCTS, ROLES, DEFAULT_COMP_PLAN, calculateCommission } from '../data/compPlans';
import { format } from 'date-fns';

const STATUS_OPTS = ['Written', 'Processing', 'Completed', 'Cancelled'];
const STATUS_COLORS = { Written: '#2a3a4a', Processing: '#2a2a1a', Completed: '#1a2e24', Cancelled: '#2a1515' };
const STATUS_TEXT = { Written: '#5599cc', Processing: '#f5a623', Completed: '#00d4a0', Cancelled: '#ff6b6b' };

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [compPlans, setCompPlans] = useState([DEFAULT_COMP_PLAN]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRep, setFilterRep] = useState('');
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }
  const [form, setForm] = useState({
    customerName: '', productId: 'gig', repId: '', compPlanId: 'synergy_1_31_26',
    quantity: 1, status: 'Written', notes: '', date: format(new Date(), 'yyyy-MM-dd'),
    overrides: [{ repId: '', role: 'Rep' }],
  });

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap, planSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'compPlans')),
      ]);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const dbPlans = planSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (dbPlans.length) setCompPlans([DEFAULT_COMP_PLAN, ...dbPlans]);
      setLoading(false);
    }
    load();
  }, []);

  function addOverride() {
    setForm(f => ({ ...f, overrides: [...f.overrides, { repId: '', role: 'Agent' }] }));
  }
  function removeOverride(i) {
    setForm(f => ({ ...f, overrides: f.overrides.filter((_, idx) => idx !== i) }));
  }
  function updateOverride(i, field, val) {
    setForm(f => {
      const overrides = [...f.overrides];
      overrides[i] = { ...overrides[i], [field]: val };
      return { ...f, overrides };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const product = SPECTRUM_PRODUCTS.find(p => p.id === form.productId);
    const primaryRep = reps.find(r => r.id === form.overrides[0]?.repId);
    const commissions = calculateCommission({ ...form, productId: form.productId }, reps, compPlans);
    const totalCommission = commissions.reduce((a, c) => a + c.amount, 0);
    const newOrder = {
      ...form,
      productName: product?.name || form.productId,
      repName: primaryRep?.name || '',
      commissions,
      totalCommission,
      createdAt: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, 'orders'), newOrder);
    setOrders(prev => [{ id: ref.id, ...newOrder, createdAt: { seconds: Date.now() / 1000 } }, ...prev]);
    setShowForm(false);
    setForm(f => ({ ...f, customerName: '', notes: '' }));
  }

  async function updateStatus(id, status) {
    await updateDoc(doc(db, 'orders', id), { status });
    setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
  }

  const repNames = [...new Set(orders.map(o => o.repName).filter(Boolean))].sort();

  const filtered = orders
    .filter(o => {
      const q = search.toLowerCase();
      return (!filterStatus || o.status === filterStatus) &&
        (!filterRep || o.repName === filterRep) &&
        (!q || o.customerName?.toLowerCase().includes(q) || o.repName?.toLowerCase().includes(q) || o.productName?.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'customer':   av = a.customerName || ''; bv = b.customerName || ''; break;
        case 'product':    av = a.productName || ''; bv = b.productName || ''; break;
        case 'rep':        av = a.repName || ''; bv = b.repName || ''; break;
        case 'commission': av = a.totalCommission || 0; bv = b.totalCommission || 0; break;
        case 'status':     av = a.status || ''; bv = b.status || ''; break;
        case 'date':       av = a.date || ''; bv = b.date || ''; break;
        default:           av = a.date || ''; bv = b.date || '';
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Orders</h1>
          <p style={styles.sub}>{orders.length} total orders</p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancel' : '+ Add order'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formTitle}>New order</div>
          <div className="form-grid" style={styles.formGrid}>
            <Field label="Customer name">
              <input style={styles.input} value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))} required />
            </Field>
            <Field label="Product">
              <select style={styles.input} value={form.productId} onChange={e => setForm(f => ({ ...f, productId: e.target.value }))}>
                {SPECTRUM_PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Comp plan">
              <select style={styles.input} value={form.compPlanId} onChange={e => setForm(f => ({ ...f, compPlanId: e.target.value }))}>
                {compPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={styles.input} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Quantity">
              <input style={styles.input} type="number" min={1} value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: +e.target.value }))} />
            </Field>
            <Field label="Date">
              <input style={styles.input} type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </Field>
          </div>

          <div style={styles.overridesSection}>
            <div style={styles.overridesLabel}>Earners (up to 5)</div>
            {form.overrides.map((ov, i) => (
              <div key={i} style={styles.overrideRow}>
                <span style={styles.overrideNum}>{i + 1}</span>
                <select style={{ ...styles.input, flex: 2 }} value={ov.repId} onChange={e => updateOverride(i, 'repId', e.target.value)} required={i === 0}>
                  <option value="">— select rep —</option>
                  {reps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <select style={{ ...styles.input, flex: 1 }} value={ov.role} onChange={e => updateOverride(i, 'role', e.target.value)}>
                  {ROLES.map(r => <option key={r}>{r}</option>)}
                </select>
                {i > 0 && <button type="button" style={styles.removeBtn} onClick={() => removeOverride(i)}>✕</button>}
              </div>
            ))}
            {form.overrides.length < 5 && (
              <button type="button" style={styles.addOverrideBtn} onClick={addOverride}>+ Add earner</button>
            )}
          </div>

          <Field label="Notes">
            <input style={styles.input} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
          </Field>

          <button type="submit" style={styles.submitBtn}>Save order</button>
        </form>
      )}

      <div style={styles.toolbar}>
        <input style={{ ...styles.input, width: 220 }} placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...styles.input, width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select style={{ ...styles.input, width: 200 }} value={filterRep} onChange={e => setFilterRep(e.target.value)}>
          <option value="">All reps</option>
          {repNames.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        {(search || filterStatus || filterRep) && (
          <button style={styles.clearBtn} onClick={() => { setSearch(''); setFilterStatus(''); setFilterRep(''); }}>
            ✕ Clear
          </button>
        )}
        <span style={styles.countBadge}>{filtered.length.toLocaleString()} of {orders.length.toLocaleString()}</span>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              {[
                { key: 'customer', label: 'Customer' },
                { key: 'product', label: 'Product' },
                { key: 'rep', label: 'Rep' },
                { key: 'commission', label: 'Commission' },
                { key: 'status', label: 'Status' },
                { key: 'date', label: 'Date' },
              ].map(col => (
                <th key={col.key} style={{ ...styles.th, cursor: 'pointer', userSelect: 'none', color: sortCol === col.key ? '#00d4a0' : '#555' }}
                  onClick={() => toggleSort(col.key)}>
                  {col.label} {sortCol === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                </th>
              ))}
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id}>
                <td style={styles.td}>{o.customerName}</td>
                <td style={{ ...styles.td, color: '#888' }}>{o.productName}</td>
                <td style={styles.td}>{o.repName}</td>
                <td style={{ ...styles.td, color: '#00d4a0' }}>${Math.round(o.totalCommission || 0).toLocaleString()}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, background: STATUS_COLORS[o.status], color: STATUS_TEXT[o.status] }}>
                    {o.status}
                  </span>
                </td>
                <td style={{ ...styles.td, color: '#555' }}>{o.date || '—'}</td>
                <td style={styles.td}>
                  <select style={styles.miniSelect} value={o.status} onChange={e => updateStatus(o.id, e.target.value)}>
                    {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#444', padding: 32 }}>No orders found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const styles = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  addBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '9px 18px', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  formCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '24px', marginBottom: 20 },
  formTitle: { fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 16 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16 },
  input: { width: '100%', background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  overridesSection: { margin: '16px 0', background: '#0f0f18', borderRadius: 10, padding: '14px 16px' },
  overridesLabel: { fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10, fontWeight: 500 },
  overrideRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 },
  overrideNum: { fontSize: 11, color: '#555', width: 16, textAlign: 'center' },
  removeBtn: { background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', fontSize: 14 },
  addOverrideBtn: { background: 'transparent', border: '1px dashed #2a2a3a', borderRadius: 7, padding: '6px 14px', color: '#555', fontSize: 12, cursor: 'pointer', marginTop: 4 },
  submitBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 12 },
  toolbar: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  clearBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8, padding: '8px 12px', color: '#555', fontSize: 12, cursor: 'pointer' },
  countBadge: { fontSize: 12, color: '#555', background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 6, padding: '6px 12px', whiteSpace: 'nowrap' },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '11px 14px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
  badge: { fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 6 },
  miniSelect: { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 6, padding: '4px 8px', color: '#888', fontSize: 11, cursor: 'pointer' },
};
